/**
 * Local Folder Input Module
 * Handles drag-drop and file picker for local folder analysis
 */

import { CODE_EXTENSIONS, shouldSkipDirectory } from '../../core/scanner';
import type { ProgressCallback } from '../../core/types';

/**
 * Check if File System Access API is available
 */
export function hasFileSystemAccess(): boolean {
  return 'showDirectoryPicker' in window;
}

/**
 * Open a directory using the File System Access API (modern browsers)
 */
export async function openDirectoryPicker(
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  if (!hasFileSystemAccess()) {
    throw new Error('File System Access API not supported');
  }

  const dirHandle = await (window as any).showDirectoryPicker();
  return readDirectoryHandle(dirHandle, '', onProgress);
}

/**
 * Read files from a FileSystemDirectoryHandle
 */
async function readDirectoryHandle(
  dirHandle: FileSystemDirectoryHandle,
  basePath: string,
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  const fileMap = new Map<string, string>();
  const entries: Array<{ path: string; handle: FileSystemFileHandle }> = [];

  // First, collect all file entries
  await collectFileEntries(dirHandle, basePath, entries);

  // Then read them with progress
  let current = 0;
  const total = entries.length;

  for (const { path, handle } of entries) {
    current++;
    if (onProgress) {
      onProgress({
        phase: 'reading',
        current,
        total,
        message: `Reading ${path}`
      });
    }

    try {
      const file = await handle.getFile();
      const content = await file.text();
      fileMap.set(path, content);
    } catch (err) {
      console.warn(`Failed to read ${path}:`, err);
    }
  }

  return fileMap;
}

/**
 * Recursively collect file entries from a directory
 */
async function collectFileEntries(
  dirHandle: FileSystemDirectoryHandle,
  basePath: string,
  entries: Array<{ path: string; handle: FileSystemFileHandle }>
): Promise<void> {
  for await (const entry of (dirHandle as any).values()) {
    const name = entry.name;
    const path = basePath ? `${basePath}/${name}` : name;

    if (entry.kind === 'directory') {
      // Skip ignored directories
      if (shouldSkipDirectory(name)) continue;

      await collectFileEntries(entry as FileSystemDirectoryHandle, path, entries);
    } else if (entry.kind === 'file') {
      // Check extension
      const ext = name.includes('.') ? '.' + name.split('.').pop()!.toLowerCase() : '';
      if (CODE_EXTENSIONS.has(ext)) {
        entries.push({ path, handle: entry as FileSystemFileHandle });
      }
    }
  }
}

/**
 * Read files from a DataTransferItemList (drag & drop)
 */
export async function readDroppedItems(
  items: DataTransferItemList,
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  const fileMap = new Map<string, string>();
  const entries: Array<{ path: string; entry: FileSystemEntry | File }> = [];

  // Convert items to entries
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.kind === 'file') {
      // Try to get as file system entry (for directories)
      const entry = item.webkitGetAsEntry?.();

      if (entry) {
        await collectDroppedEntries(entry, '', entries);
      } else {
        // Fallback to file
        const file = item.getAsFile();
        if (file) {
          const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';
          if (CODE_EXTENSIONS.has(ext)) {
            entries.push({ path: file.name, entry: file });
          }
        }
      }
    }
  }

  // Read files with progress
  let current = 0;
  const total = entries.length;

  for (const { path, entry } of entries) {
    current++;
    if (onProgress) {
      onProgress({
        phase: 'reading',
        current,
        total,
        message: `Reading ${path}`
      });
    }

    try {
      let content: string;

      if (entry instanceof File) {
        content = await entry.text();
      } else {
        content = await readFileEntry(entry as FileSystemFileEntry);
      }

      fileMap.set(path, content);
    } catch (err) {
      console.warn(`Failed to read ${path}:`, err);
    }
  }

  return fileMap;
}

/**
 * Recursively collect dropped entries
 */
async function collectDroppedEntries(
  entry: FileSystemEntry,
  basePath: string,
  entries: Array<{ path: string; entry: FileSystemEntry | File }>
): Promise<void> {
  const path = basePath ? `${basePath}/${entry.name}` : entry.name;

  if (entry.isDirectory) {
    // Skip ignored directories
    if (shouldSkipDirectory(entry.name)) return;

    const dirReader = (entry as FileSystemDirectoryEntry).createReader();
    const subEntries = await readDirectoryEntries(dirReader);

    for (const subEntry of subEntries) {
      await collectDroppedEntries(subEntry, path, entries);
    }
  } else if (entry.isFile) {
    const ext = entry.name.includes('.') ? '.' + entry.name.split('.').pop()!.toLowerCase() : '';
    if (CODE_EXTENSIONS.has(ext)) {
      entries.push({ path, entry });
    }
  }
}

/**
 * Read all entries from a directory reader
 */
function readDirectoryEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const allEntries: FileSystemEntry[] = [];

    function readBatch() {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            resolve(allEntries);
          } else {
            allEntries.push(...entries);
            readBatch(); // Continue reading
          }
        },
        reject
      );
    }

    readBatch();
  });
}

/**
 * Read content from a FileSystemFileEntry
 */
function readFileEntry(entry: FileSystemFileEntry): Promise<string> {
  return new Promise((resolve, reject) => {
    entry.file(
      (file) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      },
      reject
    );
  });
}

/**
 * Read files from a file input with webkitdirectory attribute
 * Fallback for browsers without File System Access API
 */
export async function readInputFiles(
  files: FileList,
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  const fileMap = new Map<string, string>();

  // Filter files
  const validFiles: File[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    // Get relative path (webkitRelativePath includes folder structure)
    const relativePath = (file as any).webkitRelativePath || file.name;

    // Skip ignored directories
    const pathParts = relativePath.split('/');
    let skip = false;
    for (const part of pathParts.slice(0, -1)) {
      if (shouldSkipDirectory(part)) {
        skip = true;
        break;
      }
    }
    if (skip) continue;

    // Check extension
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';
    if (CODE_EXTENSIONS.has(ext)) {
      validFiles.push(file);
    }
  }

  // Read files with progress
  let current = 0;
  const total = validFiles.length;

  for (const file of validFiles) {
    current++;
    const relativePath = (file as any).webkitRelativePath || file.name;

    // Remove the root folder name from path
    const pathParts = relativePath.split('/');
    const cleanPath = pathParts.length > 1 ? pathParts.slice(1).join('/') : relativePath;

    if (onProgress) {
      onProgress({
        phase: 'reading',
        current,
        total,
        message: `Reading ${cleanPath}`
      });
    }

    try {
      const content = await file.text();
      fileMap.set(cleanPath, content);
    } catch (err) {
      console.warn(`Failed to read ${cleanPath}:`, err);
    }
  }

  return fileMap;
}

/**
 * Create a drop zone helper
 */
export function setupDropZone(
  element: HTMLElement,
  onDrop: (files: Map<string, string>) => void,
  onProgress?: ProgressCallback
): () => void {
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    element.classList.add('drag-over');
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    element.classList.remove('drag-over');
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    element.classList.remove('drag-over');

    if (e.dataTransfer?.items) {
      const files = await readDroppedItems(e.dataTransfer.items, onProgress);
      onDrop(files);
    }
  };

  element.addEventListener('dragover', handleDragOver);
  element.addEventListener('dragleave', handleDragLeave);
  element.addEventListener('drop', handleDrop);

  // Return cleanup function
  return () => {
    element.removeEventListener('dragover', handleDragOver);
    element.removeEventListener('dragleave', handleDragLeave);
    element.removeEventListener('drop', handleDrop);
  };
}
